import type { Market } from "../domain/location/types";
import { REFERENCE_DESIGNS } from "../pages/articles/referenceDesignsData";

export interface SeoHead {
	title: string;
	description: string;
	lang: "en" | "sv";
	baseUrl: string;
	canonicalUrl: string;
	siteName: string;
	ogImage: string;
	ogImageType: string;
	ogImageWidth: string;
	ogImageHeight: string;
	ogImageAlt: string;
	twitterDescription: string;
	ogType: "website" | "article";
	alternates: SeoAlternate[];
}

export interface SeoAlternate {
	hreflang: "en" | "sv" | "x-default";
	href: string;
}

interface MarketSeo {
	title: string;
	description: string;
	lang: "en" | "sv";
	baseUrl: string;
	siteName: string;
	ogImage: string;
	ogImageType: string;
	ogImageWidth: string;
	ogImageHeight: string;
	ogImageAlt: string;
	twitterDescription: string;
}

interface RouteSeo {
	title: string;
	description: string;
}

/**
 * Year shown in time-sensitive titles (prices, rules, sizing). Bump it once a
 * year together with a content review of those pages.
 */
export const SEO_YEAR = 2026;
const Y = SEO_YEAR;

const MARKET_SEO: Record<Market, MarketSeo> = {
	se: {
		title: "Bygg en bastu – rita utomhusbastun i 3D med materiallista",
		description:
			"Bygg din egen utomhusbastu med professionella beräkningar, 3D-visualisering och materiallistor enligt svenska byggregler.",
		lang: "sv",
		baseUrl: "https://byggenbastu.se",
		siteName: "Bygg en bastu",
		ogImage:
			"https://byggenbastu.se/assets/og-image-byggenbastu-hero-20260428.jpg",
		ogImageType: "image/jpeg",
		ogImageWidth: "1200",
		ogImageHeight: "630",
		ogImageAlt:
			"Bygg en bastu - 3D-planering, materiallista och byggplan för en svensk utomhusbastu.",
		twitterDescription:
			"Planera en utomhusbastu med 3D, materiallistor och byggplan.",
	},
	intl: {
		title: "Sauna Creator – free 3D outdoor sauna planner",
		description:
			"Sketch an outdoor sauna with live 3D, structural calculations, and material takeoffs you can verify locally.",
		lang: "en",
		baseUrl: "https://saunacreator.com",
		siteName: "Sauna Creator",
		ogImage: "https://saunacreator.com/assets/og-image-1024x1024.jpg",
		ogImageType: "image/jpeg",
		ogImageWidth: "1024",
		ogImageHeight: "1024",
		ogImageAlt:
			"Sauna Creator - plan an outdoor sauna with live 3D and material lists.",
		twitterDescription:
			"Plan an outdoor sauna with live 3D, structural calculations, and material lists.",
	},
};

const ROUTE_SEO: Partial<Record<Market, Record<string, RouteSeo>>> = {
	se: {
		"/guides": {
			title: "Guider – bygga bastu, bygglov, material, kostnad | Bygg en bastu",
			description:
				"Guider för dig som bygger bastu själv: bygglov, kostnad, material, ventilation, elaggregat, vedeldad bastu och takdimension – samlat på ett ställe.",
		},
		"/guide/bygga-bastu": {
			title: `Bygga bastu – guide för utomhusbastu ${Y} | Bygg en bastu`,
			description:
				"Steg-för-steg-guide till att bygga en egen utomhusbastu. Lär dig om stomme, isolering, aggregat, ventilation, bygglov och vanliga misstag – allt på ett ställe.",
		},
		"/guide/bastu-kostnad": {
			title: `Vad kostar det att bygga bastu? Prisguide ${Y} | Bygg en bastu`,
			description:
				"Fullständig prisguide för att bygga bastu – självbygge vs hantverkare, kostnad per m², materialkalkyler och tips för att hålla budgeten.",
		},
		"/guide/bygglov-bastu": {
			title: `Bygglov för bastu ${Y} – nya regler | Bygg en bastu`,
			description:
				"Behöver bastun bygglov? Sedan 1 december 2025 är komplementbyggnader upp till 30 m² inom detaljplan (50 m² utanför) bygglovsfria. Tomtgräns och eldstad.",
		},
		"/guide/bastupanel": {
			title: `Bastupanel ${Y} – asp, al eller gran? | Bygg en bastu`,
			description:
				"Bastupanel jämförelse: asp, al, gran. Pris per m², kvistklass, värmekänsla och vad som passar lavar respektive vägg. Komplett guide för utomhusbastu.",
		},
		"/guide/ventilation-bastu": {
			title: `Ventilation i bastu ${Y} – tilluft och frånluft | Bygg en bastu`,
			description:
				"Bastuventilation: var ska tilluft och frånluft sitta? Bastufolie (ångspärr), självdrag vs mekanisk, dimensioner och vanliga misstag som leder till mögel.",
		},
		"/guide/elaggregat": {
			title: `Elaggregat bastu – kW-dimensionering ${Y} | Bygg en bastu`,
			description:
				"Hur stort elaggregat till bastun? Komplett kW-guide: m³-formel, säkring, fas, antal stenar och vanliga misstag. Snabbtabell för 6–12 kW.",
		},
		"/guide/vedeldad-bastu": {
			title: `Vedeldad bastu ${Y} – kamin och skorsten | Bygg en bastu`,
			description:
				"Vedeldad bastu: välja kamin (kW), skorstenshöjd, eldstadsanmälan och sotare. Komplett guide för svenska DIY-bastubyggare.",
		},
		"/guide/snozon-bastu": {
			title: `Snözon bastu ${Y} – takdimension per region | Bygg en bastu`,
			description:
				"Snözoner i Sverige (1,0–4,5) och takbjälksdimension för utomhusbastu. Spann-tabell, c/c-mått och när du behöver limträbalk istället för regel.",
		},
		"/guide/bastutunna-vs-fyrkantig": {
			title: `Bastutunna eller fyrkantig bastu ${Y} | Bygg en bastu`,
			description:
				"Bastutunna vs fyrkantig: pris, byggsvårighet, värmehastighet, plats och hållbarhet. Vilken passar din tomt och budget? Komplett guide.",
		},
		...Object.fromEntries(
			REFERENCE_DESIGNS.map((design) => [
				design.path,
				{ title: design.seoTitle, description: design.seoDescription },
			]),
		),
		"/calculator/heater": {
			title: `Bastuaggregat kW-kalkylator ${Y} | Bygg en bastu`,
			description:
				"Räkna ut hur många kW ditt bastuaggregat behöver. Tar hänsyn till volym, glasareal och sten. Gratis online — öppna direkt som komplett bastuplan.",
		},
		"/calculator/snow-zone": {
			title: "Snözon-kalkylator – takbjälkar per postnummer | Bygg en bastu",
			description:
				"Slå upp snözon och rekommenderad takbjälksdimension (C24) per svenskt postnummer. Gratis kalkylator för utomhusbastu och bygglovsbefriade komplementbyggnader.",
		},
		"/calculator/running-cost": {
			title: `Driftkostnad bastu – kalkylator ${Y} | Bygg en bastu`,
			description:
				"Räkna ut bastuns driftkostnad per session och månad. Gratis kalkylator: el- eller vedaggregat, uppvärmningstid, kWh per session och månadskostnad.",
		},
		"/about": {
			title: "Om Bygg en bastu – gratis planerare för utomhusbastu",
			description:
				"Bygg en bastu är ett gratis verktyg för att planera utomhusbastu: rita i 3D, dimensionera aggregatet och få materiallistor enligt svenska byggregler.",
		},
		"/contact": {
			title: "Kontakta oss | Bygg en bastu",
			description:
				"Kontakta teamet bakom Bygg en bastu om planeringsverktyget för utomhusbastu – frågor, feedback, samarbeten eller press. Vi svarar oftast inom några dagar.",
		},
		"/press": {
			title: "Press och mediakit | Bygg en bastu",
			description:
				"Pressinformation, kort boilerplate och bildlänkar för Bygg en bastu.",
		},
		"/privacy": {
			title: "Integritetspolicy | Bygg en bastu",
			description:
				"Vilka uppgifter Bygg en bastu behandlar: konto- och projektdata i Supabase (EU), integritetsvänlig statistik och enbart nödvändiga cookies för inloggning.",
		},
	},
	intl: {
		"/guides": {
			title: "Sauna planning guides for DIY builders | Sauna Creator",
			description:
				"International sauna planning guides for DIY builders: layout, permits, heater sizing, ventilation, and material decisions.",
		},
		"/guide/build-a-sauna": {
			title: "Build a sauna: practical DIY planning guide | Sauna Creator",
			description:
				"Plan an outdoor sauna from footprint to first heat-up: foundation, framing, insulation, ventilation, heater sizing, and material decisions.",
		},
		"/guide/sauna-cost": {
			title: `Outdoor sauna cost ${Y}: line-item breakdown | Sauna Creator`,
			description:
				"How much does it cost to build an outdoor sauna? Block-by-block materials from two real layouts, heater and chimney costs, and what moves the total.",
		},
		"/guide/sauna-permits": {
			title: "Sauna permits: international checklist | Sauna Creator",
			description:
				"Understand when an outdoor sauna may need zoning approval, building permits, electrical sign-off, fire clearances, or environmental review.",
		},
		"/guide/sauna-heater-sizing": {
			title: "Sauna heater sizing: choose the right kW | Sauna Creator",
			description:
				"Estimate sauna heater power from room volume, glass, insulation, climate, and warm-up expectations before choosing a heater.",
		},
		"/guide/sauna-wood-types": {
			title: "Sauna wood types: cedar, aspen, spruce | Sauna Creator",
			description:
				"Compare common sauna wood types for benches, walls, ceilings, and exterior cladding before choosing materials for an outdoor sauna.",
		},
		"/guide/sauna-ventilation": {
			title: "Sauna ventilation: intake and exhaust placement | Sauna Creator",
			description:
				"Plan a practical sauna ventilation path with intake air, exhaust placement, heater convection, and drying airflow for electric or wood-fired saunas.",
		},
		"/calculator/heater": {
			title: `Sauna Heater kW Calculator ${Y} | Sauna Creator`,
			description:
				"Work out exactly how many kW your sauna heater needs. Accounts for volume, glass area and exposed stone. Free online — open directly as a full sauna plan.",
		},
		"/calculator/snow-zone": {
			title:
				"Snow Zone Calculator – roof joist sizing for Swedish postal codes | Sauna Creator",
			description:
				"Look up the snow zone and recommended C24 roof joist for any Swedish postal code. Free calculator for outdoor saunas and permit-exempt outbuildings.",
		},
		"/calculator/running-cost": {
			title: `Sauna Running Cost Calculator ${Y} | Sauna Creator`,
			description:
				"Work out the real running cost of your sauna per session and per month. Free calculator: electric or wood-fired, heat-up time, kWh per session, monthly cost.",
		},
		"/about": {
			title: "About Sauna Creator – free outdoor sauna planner",
			description:
				"Sauna Creator is a free tool for planning outdoor saunas: design in live 3D, size your heater, and get material lists you can verify locally before you build.",
		},
		"/contact": {
			title: "Contact us | Sauna Creator",
			description:
				"Contact the Sauna Creator team about the outdoor sauna planning tool — questions, feedback, partnerships, or press. We reply to most messages within a few days.",
		},
		"/press": {
			title: "Press and media kit | Sauna Creator",
			description:
				"Press information, short boilerplate, and media links for Sauna Creator and Bygg en bastu.",
		},
		"/privacy": {
			title: "Privacy policy | Sauna Creator",
			description:
				"What data Sauna Creator processes: account and project data in Supabase (EU region), privacy-first analytics, and only the cookies needed to sign in.",
		},
	},
};

const SHARED_ALTERNATE_PATHS = new Set([
	"/",
	"/about",
	"/privacy",
	"/contact",
	"/press",
	"/guides",
	"/calculator/heater",
	"/calculator/running-cost",
]);

/**
 * Market-specific guides that cover the same question for each audience.
 * Only pairs whose content is genuinely equivalent belong here: the Swedish
 * bygglov guide is Swedish law, so it has no English twin.
 */
const PAIRED_GUIDES: ReadonlyArray<{ sv: string; en: string }> = [
	{ sv: "/guide/bygga-bastu", en: "/guide/build-a-sauna" },
	{ sv: "/guide/bastu-kostnad", en: "/guide/sauna-cost" },
	{ sv: "/guide/elaggregat", en: "/guide/sauna-heater-sizing" },
	{ sv: "/guide/ventilation-bastu", en: "/guide/sauna-ventilation" },
	{ sv: "/guide/bastupanel", en: "/guide/sauna-wood-types" },
];

function alternatePaths(seoPath: string): { sv: string; en: string } | null {
	if (SHARED_ALTERNATE_PATHS.has(seoPath)) return { sv: seoPath, en: seoPath };
	return (
		PAIRED_GUIDES.find((pair) => pair.sv === seoPath || pair.en === seoPath) ??
		null
	);
}

function buildAlternates(seoPath: string): SeoAlternate[] {
	const paths = alternatePaths(seoPath);
	if (!paths) return [];
	const url = (base: string, path: string) =>
		path === "/" ? `${base}/` : `${base}${path}`;

	return [
		{ hreflang: "en", href: url("https://saunacreator.com", paths.en) },
		{ hreflang: "sv", href: url("https://byggenbastu.se", paths.sv) },
		{ hreflang: "x-default", href: url("https://saunacreator.com", paths.en) },
	];
}

export function normalizeSeoPath(path: string): string {
	const withoutQuery = path.split(/[?#]/, 1)[0] || "/";
	if (withoutQuery === "/") return "/";
	return withoutQuery.replace(/\/+$/, "");
}

export function getSeoHead(path: string, market: Market): SeoHead {
	const seoPath = normalizeSeoPath(path);
	const currentRouteSeo = ROUTE_SEO[market]?.[seoPath];
	const otherMarket = market === "se" ? "intl" : "se";
	const otherRouteSeo = ROUTE_SEO[otherMarket]?.[seoPath];
	const ownerMarket = currentRouteSeo || !otherRouteSeo ? market : otherMarket;
	const marketSeo = MARKET_SEO[ownerMarket];
	const routeSeo = currentRouteSeo ?? otherRouteSeo;
	const canonicalUrl =
		seoPath === "/"
			? `${marketSeo.baseUrl}/`
			: `${marketSeo.baseUrl}${seoPath}`;

	return {
		...marketSeo,
		title: routeSeo?.title ?? marketSeo.title,
		description: routeSeo?.description ?? marketSeo.description,
		twitterDescription: routeSeo?.description ?? marketSeo.twitterDescription,
		canonicalUrl,
		ogType:
			seoPath.startsWith("/guide/") || seoPath.startsWith("/ritningar/")
				? "article"
				: "website",
		alternates: buildAlternates(seoPath),
	};
}
